import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext, AuthState, AuthContextType } from '../contexts/AuthContext';
import { User } from '../../entities/user/types';
import { Company } from '../../entities/company/types';
import { authApi, companiesApi } from '../../services/http';
import { setTokens, clearTokens, getAccessToken } from '../../services/http/apiClient';
import { login as loginApi, logout as logoutApi } from '../../services/http/authApi';
import { hasPermission, Role } from '../../config/roles';
import type { Company as ApiCompany, CurrentSessionResponse } from '../../api/types';

const SESSION_STORAGE_KEY = 'fathy_pesage_session';

interface SessionData {
  accessToken: string;
  refreshToken: string;
}

function mapSessionUser(session: CurrentSessionResponse): User {
  return {
    id: session.userId,
    email: session.email,
    firstName: session.firstName,
    lastName: session.lastName,
    role: session.role,
    companyId: session.companyId,
    mustChangePassword: session.mustChangePassword,
    passwordResetRequested: false,
    isActive: true,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };
}

function mapSessionCompany(session: CurrentSessionResponse): Company | null {
  if (!session.companyId || !session.companySlug || !session.companyName) {
    return null;
  }

  return {
    id: session.companyId,
    slug: session.companySlug,
    name: session.companyName,
    address: '',
    phone: '',
    email: '',
    isActive: true,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };
}

function mapCompany(company: ApiCompany): Company {
  return {
    id: company.id,
    slug: company.slug,
    name: company.name,
    address: company.address,
    phone: company.phone,
    email: company.email,
    isActive: company.isActive,
    createdAt: new Date(company.createdAt),
    updatedAt: new Date(company.updatedAt),
  };
}

function saveSession(accessToken: string, refreshToken: string): void {
  const data: SessionData = { accessToken, refreshToken };
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  setTokens(accessToken, refreshToken);
}

function loadSession(): SessionData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionData;
    if (parsed.accessToken && parsed.refreshToken) {
      setTokens(parsed.accessToken, parsed.refreshToken);
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  clearTokens();
}

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    company: null,
  });

  const loadCompany = useCallback(async (companyId: string | null): Promise<Company | null> => {
    if (!companyId) {
      return null;
    }

    try {
      const company = await companiesApi.getCompanyById(companyId);
      return mapCompany(company);
    } catch {
      return null;
    }
  }, []);

  const loadSessionContext = useCallback(async () => {
    const response = await authApi.getCurrentUser();
    const user = mapSessionUser(response);
    const company = mapSessionCompany(response) ?? await loadCompany(response.companyId);
    return { user, company };
  }, [loadCompany]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const { user, company } = await loadSessionContext();
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user,
        company,
      });
    } catch {
      // Session invalid, clear and redirect to login
      clearSession();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        company: null,
      });
    }
  }, [loadSessionContext]);

  useEffect(() => {
    const initializeAuth = async () => {
      const session = loadSession();
      if (session && getAccessToken()) {
        // We have tokens, try to load the user
        await loadCurrentUser();
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          company: null,
        });
      }
    };

    initializeAuth();
  }, [loadCurrentUser]);

  const login = async (email: string, password: string) => {
    const response = await loginApi({ email, password });
    saveSession(response.accessToken, response.refreshToken);
    const { user, company } = await loadSessionContext();
    setAuthState({
      isAuthenticated: true,
      isLoading: false,
      user,
      company,
    });

    return user;
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch {
      // ignore server errors on logout
    }
    clearSession();
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      company: null,
    });
  };

  const hasRole = (role: string | string[]): boolean => {
    if (!authState.user) return false;

    if (Array.isArray(role)) {
      return role.includes(authState.user.role);
    }
    return authState.user.role === role;
  };

  const can = (permission: string): boolean => {
    if (!authState.user) return false;
    return hasPermission(authState.user.role as Role, permission);
  };

  const switchCompany = (_company: Company) => {
    if (authState.user) {
      setAuthState((prev) => ({
        ...prev,
        company: _company,
      }));
    }
  };

  const contextValue: AuthContextType = {
    authState,
    login,
    logout,
    hasRole,
    can,
    switchCompany,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { AuthProvider };
